import React, { useMemo, useState } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import { Search } from 'lucide-react';
import { KeyType, RegisterKey } from '../../types/keyRegister';
import { keyCountsByBuilding, KeyScopeOptions } from './keyRegisterView';
import { KeyObjectsByBuilding } from './keyObjects';
import { EMPTY_SELECTION, isSelectionEmpty, KeySelection } from './keySelection';
import KeyPlaceTree from './KeyPlaceTree';

interface KeyFilterModalProps {
    show: boolean;
    /** Changes on every open, so each open starts a fresh draft (also while the last close still animates). */
    openCount: number;
    onHide: () => void;
    buildings: Array<{ key: string; name: string }>;
    objectsByBuilding: KeyObjectsByBuilding;
    loadingBuildings: { [buildingKey: string]: true };
    linkedObjectKeys: ReadonlySet<string>;
    types: KeyType[];
    keysByType: { [typeKey: string]: RegisterKey[] };
    selection: KeySelection;
    options: KeyScopeOptions;
    onApply: (selection: KeySelection, options: KeyScopeOptions) => void;
}

const NO_OPTIONS: KeyScopeOptions = { showScrapped: false, showArchived: false };

/**
 * The nyckelregister's filters behind the toolbar's Filter button: the place tree and the scope
 * toggles. Changes are kept in the modal until Visa nycklar; closing discards them.
 */
const KeyFilterModal: React.FC<KeyFilterModalProps> = ({ show, openCount, onHide, ...form }) => (
    <Modal show={show} onHide={onHide} centered scrollable className="key-filter-modal">
        <KeyFilterForm key={openCount} onHide={onHide} {...form} />
    </Modal>
);

const KeyFilterForm: React.FC<Omit<KeyFilterModalProps, 'show' | 'openCount'>> = ({
    onHide, buildings, objectsByBuilding, loadingBuildings, linkedObjectKeys, types, keysByType, selection, options, onApply,
}) => {
    const [draft, setDraft] = useState(selection);
    const [draftOptions, setDraftOptions] = useState(options);
    const [query, setQuery] = useState('');
    const keyCounts = useMemo(() => keyCountsByBuilding(types, keysByType, draftOptions), [types, keysByType, draftOptions]);

    const anySet = !isSelectionEmpty(draft) || draftOptions.showScrapped || draftOptions.showArchived;

    return (
        <>
            <Modal.Header closeButton>
                <Modal.Title>Filter</Modal.Title>
            </Modal.Header>
            <Modal.Body className="key-filter-body">
                <div className="key-search key-filter-search">
                    <Search size={16} className="key-search-icon" aria-hidden="true" />
                    <Form.Control
                        type="search"
                        className="key-search-input"
                        placeholder="Sök byggnad, lägenhet, lokal eller förråd…"
                        aria-label="Sök byggnad, lägenhet, lokal eller förråd"
                        value={query}
                        onChange={e => setQuery(e.target.value)}
                    />
                </div>
                <KeyPlaceTree
                    buildings={buildings}
                    objectsByBuilding={objectsByBuilding}
                    loadingBuildings={loadingBuildings}
                    linkedObjectKeys={linkedObjectKeys}
                    keyCounts={keyCounts}
                    selection={draft}
                    onChange={setDraft}
                    query={query}
                />
                <fieldset className="key-filter-options">
                    <legend className="form-label">Visa även</legend>
                    <Form.Check
                        type="checkbox"
                        id="key-register-show-scrapped"
                        className="key-toolbar-check"
                        label="Kasserade nycklar"
                        checked={draftOptions.showScrapped}
                        onChange={e => setDraftOptions(prev => ({ ...prev, showScrapped: e.target.checked }))}
                    />
                    <Form.Check
                        type="checkbox"
                        id="key-register-show-archived"
                        className="key-toolbar-check"
                        label="Arkiverade nyckeltyper"
                        checked={draftOptions.showArchived}
                        onChange={e => setDraftOptions(prev => ({ ...prev, showArchived: e.target.checked }))}
                    />
                </fieldset>
            </Modal.Body>
            <Modal.Footer className="key-filter-footer">
                {anySet && (
                    <Button variant="link" className="me-auto" onClick={() => { setDraft(EMPTY_SELECTION); setDraftOptions(NO_OPTIONS); }}>
                        Rensa filter
                    </Button>
                )}
                <Button variant="outline-secondary" onClick={onHide}>Avbryt</Button>
                <Button variant="primary" onClick={() => onApply(draft, draftOptions)}>Visa nycklar</Button>
            </Modal.Footer>
        </>
    );
};

export default KeyFilterModal;
